/* ============================================================
   Biodimension Design System — shared MUI token helpers
   ------------------------------------------------------------
   These reference the CSS variables defined in globals.css.
   Because those variables are redefined under the `.dark`
   selector, referencing them here means components adapt to
   light/dark automatically — no `mode === 'light' ? … : …`
   branching required.
   ============================================================ */

// Glass "paper" for MUI Dialogs / Popovers (modals, dropdowns).
// Pair with `className: 'glass-strong'` for the frosted backdrop.
export const glassDialogPaperProps = {
  className: 'glass-strong',
  sx: {
    borderRadius: 'var(--radius-lg)',
    backgroundImage: 'none',
    boxShadow: 'var(--shadow-e3)',
    color: 'var(--color-fg)',
  },
};

// Glass surface for large content cards (list card, panels).
export const glassCardSx = {
  background: 'var(--color-glass-bg)',
  border: '1px solid var(--color-glass-border)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-e2)',
};

// Token-based styling for text inputs / selects inside modals.
// Uses design-system surface + outline + text tokens.
export const tokenFieldSx = {
  '& .MuiOutlinedInput-root': {
    background: 'var(--color-surface-3)',
    transition: 'background 0.2s',
    '& fieldset': { borderColor: 'var(--color-outline)' },
    '&:hover': { background: 'var(--color-surface-2)' },
    '&:hover fieldset': { borderColor: 'var(--color-outline-strong)' },
    '&.Mui-focused': { background: 'var(--color-surface)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)', borderWidth: 1.5 },
  },
  '& .MuiInputBase-input': {
    color: 'var(--color-fg)',
    WebkitTextFillColor: 'var(--color-fg)',
  },
  '& .MuiInputBase-input::placeholder, & input::placeholder, & textarea::placeholder': {
    color: 'var(--color-muted)',
    opacity: 1,
  },
  // Keep read-only / disabled text fully legible in both themes
  '& .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled, & .MuiInputBase-root.Mui-disabled .MuiInputBase-input, & .MuiSelect-select.Mui-disabled':
    {
      color: 'var(--color-fg) !important',
      WebkitTextFillColor: 'var(--color-fg) !important',
      opacity: '1 !important',
    },
  '& input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px var(--color-surface-3) inset',
    WebkitTextFillColor: 'var(--color-fg) !important',
    caretColor: 'var(--color-fg)',
    borderRadius: 'inherit',
  },
};

// Subtle "sunken" surface used for read-only fields.
export const readonlyFieldBg = 'var(--color-surface-2)';

// Opaque frosted paper for dropdown menus / popovers.
// Uses a solid surface so page content behind never bleeds through,
// while keeping the frosted blur + soft elevation of the design system.
export const frostedPopoverSx = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-outline)',
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  boxShadow: 'var(--shadow-e3)',
  color: 'var(--color-fg)',
};
